import { motion } from "framer-motion";
import { useState } from "react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../i18n/LanguageContext";
import { LockKeyhole, MessageCircle, LogOut, Check, Star, CreditCard, Loader2 } from "lucide-react";
import { SaudiRiyal } from "../components/SaudiRiyal";
import { SUPPORT_WHATSAPP } from "../lib/constants";
import { api } from "../lib/api";
import { Logo } from "../components/Logo";
import { LanguageSwitcher } from "../components/LanguageSwitcher";

const PP_TXT = {
  ar: { fail: "تعذّر بدء الدفع، حاول مرة أخرى", subscribe: "اشترك الآن" },
  en: { fail: "Payment could not be started, please try again", subscribe: "Subscribe now" },
  ur: { fail: "ادائیگی شروع نہ ہو سکی، دوبارہ کوشش کریں", subscribe: "ابھی سبسکرائب کریں" },
};

export default function Paywall() {
  const { subscription, logout } = useAuth();
  const { t, dir, lang } = useLang();
  const L = PP_TXT[lang] || PP_TXT.ar;
  const wa = subscription?.support_whatsapp || SUPPORT_WHATSAPP;
  const plans = subscription?.plans || [];
  const [payingPlan, setPayingPlan] = useState(null);
  const waFor = (plan) =>
    `https://wa.me/${wa}?text=${encodeURIComponent(`مرحباً، أرغب بالاشتراك في WorkZone${plan ? ` — الباقة ${plan}` : ""}`)}`;

  const subscribe = async (p) => {
    setPayingPlan(p.id);
    try {
      const data = await api.paymobSubscribe(p.id);
      window.location.assign(data.checkout_url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || L.fail);
      setPayingPlan(null);
    }
  };

  const PayButton = ({ p }) => (
    <button data-testid={`plan-subscribe-pay-${p.id}`} disabled={payingPlan === p.id} onClick={() => subscribe(p)}
      className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-xl h-11 font-semibold bg-blue-600 hover:bg-blue-700 text-white transition disabled:opacity-60">
      {payingPlan === p.id ? <><Loader2 className="h-4 w-4 animate-spin" /> جارٍ فتح الدفع…</> : <><CreditCard className="h-4 w-4" /> {L.subscribe} — {p.price} <SaudiRiyal /></>}
    </button>
  );

  return (
    <div dir={dir} className="min-h-screen bg-gradient-to-b from-blue-900 via-slate-900 to-slate-900 py-12 px-4"
      data-testid="paywall-screen">
      <div className="mx-auto max-w-5xl">
        <div className="flex justify-end mb-4">
          <LanguageSwitcher tone="dark" />
        </div>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
          className="text-center text-white">
          <div className="flex justify-center mb-6"><Logo size="lg" textClass="text-white" /></div>
          <div className="mx-auto h-14 w-14 rounded-2xl bg-amber-400/20 text-amber-300 flex items-center justify-center">
            <LockKeyhole className="h-7 w-7" />
          </div>
          <h1 className="mt-5 font-heading text-3xl font-extrabold">{t("paywall.title")}</h1>
          <p className="mt-3 text-slate-300 max-w-xl mx-auto leading-relaxed">
            {t("paywall.subtitle")}
          </p>
        </motion.div>

        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {plans.map((p, i) => {
            const featured = p.id === "business";
            const name = t(`landing.plans.${p.id}.name`);
            const featuresRaw = t(`landing.plans.${p.id}.features`);
            const features = Array.isArray(featuresRaw) ? featuresRaw : (p.features || []);
            return (
              <motion.div key={p.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                data-testid={`plan-card-${p.id}`}
                className={`relative rounded-2xl p-6 flex flex-col ${featured ? "bg-white ring-2 ring-blue-400 shadow-2xl scale-[1.03]" : "bg-white/95"}`}>
                {featured && (
                  <span className="absolute -top-3 right-5 inline-flex items-center gap-1 rounded-full bg-blue-600 px-3 py-1 text-xs font-bold text-white">
                    <Star className="h-3 w-3" /> {t("landing.pricing.mostPopular")}
                  </span>
                )}
                {p.id === "professional" && (
                  <span className="absolute -top-3 right-5 inline-flex items-center gap-1 rounded-full bg-emerald-500 px-3 py-1 text-xs font-bold text-white">
                    <Star className="h-3 w-3 fill-white" /> {t("landing.pricing.bestValue")}
                  </span>
                )}
                <h3 className="font-heading text-lg font-extrabold text-slate-900">{name}</h3>
                <p className="text-xs text-slate-400">{t(`landing.plans.${p.id}.tagline`)}</p>
                {p.price != null ? (
                  <div className="mt-3">
                    {p.original_price && (
                      <div className="flex items-center gap-2">
                        <span className="text-base font-semibold text-slate-400 line-through decoration-rose-300 decoration-2 font-mono-nums" dir="ltr">{p.original_price}</span>
                        <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[11px] font-extrabold text-rose-600">{t("landing.pricing.discount")} {Math.round((1 - p.price / p.original_price) * 100)}%</span>
                      </div>
                    )}
                    <div className="mt-0.5 flex items-baseline gap-1">
                      <span className="text-3xl font-extrabold text-slate-900 font-mono-nums">{p.price}</span>
                      <span className="text-sm text-slate-500">{t("landing.pricing.perMonth")}</span>
                    </div>
                    <span className="mt-2 inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-1 text-[11px] font-bold text-amber-700 animate-pulse">
                      {t("landing.pricing.limitedOffer")}
                    </span>
                  </div>
                ) : (
                  <div className="mt-3 flex items-baseline gap-1">
                    <span className="text-2xl font-extrabold text-slate-900">{t("landing.pricing.perRequest")}</span>
                  </div>
                )}
                <ul className="mt-4 space-y-2 flex-1">
                  {features.map((f, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-slate-600">
                      <Check className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" /> {f}
                    </li>
                  ))}
                </ul>
                <a href={waFor(p.name)} target="_blank" rel="noreferrer" data-testid={`plan-subscribe-${p.id}`}
                  className={`mt-5 inline-flex items-center justify-center gap-2 rounded-xl h-11 font-semibold transition ${featured ? "bg-blue-600 hover:bg-blue-700 text-white" : "bg-slate-100 hover:bg-slate-200 text-slate-800"}`}>
                  <MessageCircle className="h-4 w-4" /> {t("paywall.subscribeNow")}
                </a>
                {p.price != null && <PayButton p={p} />}
              </motion.div>
            );
          })}
        </div>

        <div className="mt-10 text-center">
          <a href={waFor("")} target="_blank" rel="noreferrer" data-testid="paywall-whatsapp-button"
            className="inline-flex items-center justify-center gap-2 rounded-md h-12 px-8 bg-[#25D366] hover:bg-[#1EBE57] text-white font-semibold transition">
            <MessageCircle className="h-5 w-5" /> {t("paywall.whatsappContact")} {wa}
          </a>
          <div>
            <button data-testid="paywall-logout-button" onClick={logout}
              className="mt-5 inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-white transition">
              <LogOut className="h-4 w-4" /> {t("paywall.logout")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
